import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { ChevronLeft, ChevronRight, Download, File, FileText, ExternalLink, Expand, Minimize, Plus } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { getAdjacentResourceIds, isViewableInBrowser } from '../utils/resource-display-utils';
import { SidebarInset } from './ui/sidebar';
import { SidebarToggleButton } from './SidebarToggleButton';
import { ViewHeader } from './ViewHeader';
import { EditableResourceHeader } from './EditableResourceHeader';
import { ResourceCreateActions } from './ResourceCreateActions';
import { AnalyzeResourceButton } from './AnalyzeResourceButton';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { InfoHint } from './InfoHint';
import { useIsMobile } from '../hooks/use-mobile';
import ReactMarkdown from 'react-markdown';
import { Badge } from './ui/badge';
import { isSystemTag } from '../utils/system-tags';
import { ProjectTagsField } from './ProjectTagsField';
import { EntityTagsField, mentionableToEntityTagOption } from './EntityTagsField';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { RelatedEntityLink } from './RelatedEntityLink';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { stripHiddenLinkedEntitiesBlock, withLinkedEntityMentions, extractMentionedEntityIds } from '../utils/entity-mentions';
import { linkedEntityIdsForResource } from '../utils/resource-linked-entity-ids';
import { AddTimelineEventModal } from './AddTimelineEventModal';
import { ConfirmDialog } from './ConfirmDialog';
import { APP_VIEWPORT_HEIGHT, appViewportHeightMinus } from '../constants/layout';
import { DatePicker } from './DatePicker';
import { useProjectPageTitle } from '../hooks/usePageTitle';
import { useProjectTitle } from '../hooks/useProjectTitle';
import { parseResourceUrl } from '../utils/ensure-https-url';

const resourceInsetStyle: React.CSSProperties = {
  minWidth: 0,
  overflowX: 'hidden',
};

interface ResourceAnalysis {
  title: string;
  type: 'news' | 'article' | 'website' | 'book' | 'paper' | 'video' | 'Twitter' | 'Instagram';
  source: string;
  date: string;
  summary: string;
  keywords: string[];
  natural_id?: string;
}

interface ResourceData {
  id: string;
  fileName: string;
  title: string;
  fileSize: number | null;
  mimeType: string | null;
  downloadUrl: string | null;
  url: string | null;
  naturalId: string | null;
  resourceDate: string | null;
  notes: string | null;
  analysis: ResourceAnalysis | null;
  analysisError?: string | null;
  wordCount: number | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

const normalizeTag = (tag: string): string => tag.trim().toLowerCase();

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildResourceMentionDescription(resource: { id: string; title: string }): string {
  const safeTitle = escapeHtml(resource.title);
  return `<p><span data-type="mention" data-id="${resource.id}" data-entity-type="resource" data-label="${safeTitle}">@${safeTitle}</span></p>`;
}


const MetadataRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-w-0 max-w-full flex-wrap items-baseline gap-1.5 text-sm">
    <span className="shrink-0 font-medium text-foreground">{label}:</span>
    <span className="min-w-0 max-w-full break-all text-muted-foreground">{children}</span>
  </div>
);

const previewOverlayButtonStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 10,
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '0.5rem',
  padding: 0,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s',
};

const ResourcePreviewOverlayButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void;
    ariaLabel: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
  }
>(({ onClick, ariaLabel, style, children }, ref) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    style={{ ...previewOverlayButtonStyle, ...style }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'var(--muted)';
      e.currentTarget.style.borderColor = 'var(--border)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'var(--card)';
      e.currentTarget.style.borderColor = 'var(--border)';
    }}
    aria-label={ariaLabel}
    title={ariaLabel}
  >
    {children}
  </button>
));
ResourcePreviewOverlayButton.displayName = 'ResourcePreviewOverlayButton';

const ResourceDetailActions: React.FC<{
  canDelete: boolean;
  canDownload: boolean;
  onDelete: () => void;
  onDownload: () => void;
  deleteLabel: string;
  downloadLabel: string;
}> = ({ canDelete, canDownload, onDelete, onDownload, deleteLabel, downloadLabel }) => {
  if (!canDelete && !canDownload) {
    return null;
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: canDelete && canDownload ? 'space-between' : 'flex-end',
        alignItems: 'center',
        marginTop: '1rem',
        gap: '0.5rem',
      }}
    >
      {canDelete && (
        <Button
          variant="destructive"
          onClick={onDelete}
          className="group inline-flex items-center gap-1.5"
        >
          <RotatingDeleteIcon />
          {deleteLabel}
        </Button>
      )}
      {canDownload && (
        <Button onClick={onDownload} variant="default">
          <Download className="h-4 w-4" />
          {downloadLabel}
        </Button>
      )}
    </div>
  );
};

export const ResourceDetail: React.FC = () => {
  const { projectId, resourceId } = useParams<{ projectId: string; resourceId: string }>();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast, showFieldSavedToast } = useToast();
  const { canEditProject } = useProjectAccess();
  const isMobile = useIsMobile();
  const [resource, setResource] = useState<ResourceData | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [galleryResourceIds, setGalleryResourceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const isMaximizedRef = useRef(false);
  const maximizedContainerRef = useRef<HTMLDivElement>(null);
  isMaximizedRef.current = isMaximized;
  const [error, setError] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const [entityRefsLoaded, setEntityRefsLoaded] = useState(false);
  const [entityRefsRefreshTrigger, setEntityRefsRefreshTrigger] = useState(0);
  const [isAddTimelineModalOpen, setIsAddTimelineModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [linkIframeFailed, setLinkIframeFailed] = useState(false);
  const notesSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectTitle = useProjectTitle();
  useProjectPageTitle(projectTitle, t('Resources'), resource?.title);

  useEffect(() => {
    setLinkIframeFailed(false);
    if (!resource?.url || !projectId || !resourceId || !token) {
      return;
    }
    let cancelled = false;
    // Cross-origin iframes are opaque to JS, and X-Frame-Options/CSP blocks don't reliably
    // fire the iframe's onError event — so embeddability is checked server-side by reading
    // the target URL's response headers directly.
    const checkEmbeddable = async (): Promise<void> => {
      try {
        const res = await fetch(
          `${API_BASE}/api/projects/${projectId}/resources/${resourceId}/embeddable`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok || cancelled) {
          return;
        }
        const data = await res.json();
        if (!cancelled && data.embeddable === false) {
          setLinkIframeFailed(true);
        }
      } catch {
        // ignore — fall back to the iframe's own onError
      }
    };
    void checkEmbeddable();
    return () => {
      cancelled = true;
    };
  }, [resource?.url, resource?.id, projectId, resourceId, token]);

  useEffect(() => {
    const handleResourceUploaded = (event: Event): void => {
      const detail = (event as CustomEvent<{ resourceIds?: string[]; projectId?: string }>).detail;
      if (!detail?.resourceIds?.length || !detail.projectId || projectId !== detail.projectId) {
        return;
      }
      const createdResourceId = detail.resourceIds[detail.resourceIds.length - 1];
      if (createdResourceId && createdResourceId !== resourceId) {
        navigate(`/projects/${detail.projectId}/resources/${createdResourceId}`);
      }
    };

    window.addEventListener('resource-uploaded', handleResourceUploaded);
    return () => {
      window.removeEventListener('resource-uploaded', handleResourceUploaded);
    };
  }, [projectId, resourceId, navigate]);

  useEffect(() => {
    const fetchMentionableEntities = async (): Promise<void> => {
      if (!projectId || !token) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };
    fetchMentionableEntities();
  }, [projectId, token, entityRefsRefreshTrigger]);

  useEffect(() => {
    const fetchEntityRefs = async (): Promise<void> => {
      if (!projectId || !token) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEntityRefs(data.references ?? []);
        } else {
          setEntityRefs([]);
        }
      } catch {
        setEntityRefs([]);
      } finally {
        setEntityRefsLoaded(true);
      }
    };
    fetchEntityRefs();
  }, [projectId, token, entityRefsRefreshTrigger]);

  useEffect(() => {
    const fetchResource = async (): Promise<void> => {
      if (!projectId || !resourceId || !token) {
        return;
      }

      try {
        if (!isMaximizedRef.current) {
          setLoading(true);
        }
        setError(null);

        const resourceResponse = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!resourceResponse.ok) {
          if (resourceResponse.status === 404) {
            setError(t('Resource not found'));
          } else {
            setError(t('Failed to load resource'));
          }
          return;
        }

        const resourceData = await resourceResponse.json();
        setResource({
          ...resourceData,
          tags: Array.isArray(resourceData.tags) ? resourceData.tags : [],
        });
      } catch (err) {
        console.error('Error fetching resource:', err);
        setError(t('Error loading resource'));
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [projectId, resourceId, token, t]);

  const relatedEntities = useMemo(() => {
    if (!resourceId || !projectId) return { linksTo: [], referencedIn: [] };
    const entityLookup = new Map<string, MentionableEntity>();
    const entityLookupByTypeAndId = new Map<string, MentionableEntity>();
    for (const e of mentionableEntities) {
      const key = `${e.type}:${e.id}:${e.documentId ?? ''}`;
      entityLookup.set(key, e);
      const typeIdKey = `${e.type}:${e.id}`;
      if (!entityLookupByTypeAndId.has(typeIdKey)) {
        entityLookupByTypeAndId.set(typeIdKey, e);
      }
    }
    const resolveEntity = (
      type: string,
      id: string,
      documentId: string | null
    ): MentionableEntity | undefined => {
      return (
        entityLookup.get(`${type}:${id}:${documentId ?? ''}`) ??
        entityLookupByTypeAndId.get(`${type}:${id}`)
      );
    };
    const linksTo: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    const referencedIn: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    for (const ref of entityRefs) {
      if (ref.sourceType === 'resource' && ref.sourceId === resourceId) {
        const entity = resolveEntity(ref.targetType, ref.targetId, ref.targetDocumentId);
        if (entity) {
          linksTo.push({
            id: ref.targetId,
            type: ref.targetType,
            label: entity.label,
            documentId: ref.targetDocumentId ?? entity.documentId,
          });
        }
      }
      if (ref.targetType === 'resource' && ref.targetId === resourceId) {
        const entity = resolveEntity(ref.sourceType, ref.sourceId, ref.sourceDocumentId);
        if (entity) {
          referencedIn.push({
            id: ref.sourceId,
            type: ref.sourceType,
            label: entity.label,
            documentId: ref.sourceDocumentId ?? entity.documentId,
          });
        }
      }
    }
    const dedupe = (arr: typeof linksTo) => {
      const seen = new Set<string>();
      return arr.filter((x) => {
        const k = `${x.type}:${x.id}:${x.documentId ?? ''}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    return { linksTo: dedupe(linksTo), referencedIn: dedupe(referencedIn) };
  }, [resourceId, projectId, entityRefs, mentionableEntities]);

  const otherLinksTo = useMemo(
    () => relatedEntities.linksTo.filter((e) => e.type !== 'character' && e.type !== 'location'),
    [relatedEntities.linksTo]
  );

  const characterOptions = useMemo(
    () => mentionableEntities.filter((entity) => entity.type === 'character').map(mentionableToEntityTagOption),
    [mentionableEntities]
  );
  const locationOptions = useMemo(
    () => mentionableEntities.filter((entity) => entity.type === 'location').map(mentionableToEntityTagOption),
    [mentionableEntities]
  );
  const characterOptionsById = useMemo(() => new Map(characterOptions.map((o) => [o.id, o])), [characterOptions]);
  const locationOptionsById = useMemo(() => new Map(locationOptions.map((o) => [o.id, o])), [locationOptions]);

  // Plain, directly-settable state — same model as AddGenericResourceModal's
  // selectedCharacterIds/selectedLocationIds. Seeded once per resource from entity
  // references and notes mentions (without waiting for mentionable-entity labels),
  // then mutated by typed @mentions and manual checkbox toggles.
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const latestNotesHtmlRef = useRef<string | null>(null);
  const initializedEntityTagsResourceIdRef = useRef<string | null>(null);

  useEffect(() => {
    initializedEntityTagsResourceIdRef.current = null;
    setSelectedCharacterIds([]);
    setSelectedLocationIds([]);
    latestNotesHtmlRef.current = null;
  }, [resourceId]);

  useEffect(() => {
    if (!resourceId || !entityRefsLoaded || !resource || resource.id !== resourceId) {
      return;
    }
    if (initializedEntityTagsResourceIdRef.current === resourceId) {
      return;
    }
    initializedEntityTagsResourceIdRef.current = resourceId;
    const linked = linkedEntityIdsForResource(resourceId, entityRefs, resource.notes);
    setSelectedCharacterIds(linked.characterIds);
    setSelectedLocationIds(linked.locationIds);
  }, [resourceId, entityRefsLoaded, resource, entityRefs]);

  const handleEntityTagsChange = (
    entityType: 'character' | 'location',
    ids: string[]
  ): void => {
    if (!resource) {
      return;
    }
    if (entityType === 'character') {
      setSelectedCharacterIds(ids);
    } else {
      setSelectedLocationIds(ids);
    }
    const baseNotes = latestNotesHtmlRef.current ?? resource.notes ?? '';
    const visibleNotes = stripHiddenLinkedEntitiesBlock(baseNotes);
    const nextCharacterIds = entityType === 'character' ? ids : selectedCharacterIds;
    const nextLocationIds = entityType === 'location' ? ids : selectedLocationIds;
    const nextNotes = withLinkedEntityMentions(
      visibleNotes,
      nextCharacterIds,
      nextLocationIds,
      characterOptionsById,
      locationOptionsById
    );
    latestNotesHtmlRef.current = nextNotes;
    void handleNotesChange(nextNotes || null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isGenericResource = Boolean(
    resource && !resource.url && !resource.downloadUrl
  );

  const { previousId: previousGalleryResourceId, nextId: nextGalleryResourceId } = useMemo(
    () => getAdjacentResourceIds(galleryResourceIds, resourceId),
    [galleryResourceIds, resourceId]
  );

  const navigateToGalleryResource = useCallback((targetId: string | null): void => {
    if (!targetId || !projectId || targetId === resourceId) {
      return;
    }
    navigate(`/projects/${projectId}/resources/${targetId}`);
  }, [projectId, resourceId, navigate]);

  useEffect(() => {
    if (!isMaximized || !projectId || !token) {
      return;
    }
    let cancelled = false;
    const fetchGalleryResources = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok || cancelled) {
          return;
        }
        const data = await response.json() as {
          resources?: Array<{ id: string; mimeType: string | null; url: string | null }>;
        };
        if (cancelled) {
          return;
        }
        setGalleryResourceIds(
          (data.resources ?? [])
            .filter((item) => isViewableInBrowser(item.mimeType, item.url))
            .map((item) => item.id)
        );
      } catch {
        if (!cancelled) {
          setGalleryResourceIds([]);
        }
      }
    };
    void fetchGalleryResources();
    return () => {
      cancelled = true;
    };
  }, [isMaximized, projectId, token]);

  useEffect(() => {
    if (!isMaximized) {
      return;
    }
    maximizedContainerRef.current?.focus();
  }, [isMaximized, resourceId]);

  useEffect(() => {
    if (!isMaximized) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMaximized(false);
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return;
        }
        if (target.isContentEditable) {
          return;
        }
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateToGalleryResource(previousGalleryResourceId);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateToGalleryResource(nextGalleryResourceId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMaximized, previousGalleryResourceId, nextGalleryResourceId, navigateToGalleryResource]);

  const handleVisitLink = (): void => {
    if (resource?.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderFilePreview = (): React.ReactNode => {
    if (!resource || !isViewableInBrowser(resource.mimeType, resource.url)) {
      return null;
    }

    if (resource.url) {
      if (linkIframeFailed) {
        return null;
      }
      return (
        <iframe
          src={resource.url}
          title={resource.fileName}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'var(--card)',
          }}
          sandbox="allow-scripts allow-same-origin"
          onError={() => setLinkIframeFailed(true)}
        />
      );
    }

    if (resource.mimeType?.startsWith('image/') && resource.downloadUrl) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--card)' }}>
          <img
            src={resource.downloadUrl}
            alt={resource.fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </div>
      );
    }

    if ((resource.mimeType === 'application/pdf' || resource.mimeType === 'text/html' || resource.mimeType === 'text/plain' || resource.mimeType === 'text/markdown') && resource.downloadUrl) {
      return (
        <iframe
          src={resource.downloadUrl}
          title={resource.fileName}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'var(--card)',
          }}
        />
      );
    }

    return null;
  };

  const renderPreviewInfoPanel = (): React.ReactNode => {
    if (!resource?.url) {
      return null;
    }
    return (
      <div className="mb-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('resource_url_label')}</label>
            <div className="flex items-center gap-2">
              <Input
                key={`${resource.id}-url`}
                type="text"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                defaultValue={resource.url ?? ''}
                disabled={!canEditProject}
                className="flex-1"
                onBlur={(e) => {
                  void handleResourceUrlChange(e.target.value);
                }}
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleVisitLink}
                className="shrink-0"
              >
                <ExternalLink className="h-4 w-4" />
                {t('visit_link')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDownload = (): void => {
    if (resource?.downloadUrl) {
      window.open(resource.downloadUrl, '_blank');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!token || !projectId || !resourceId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete resource');
      }

      showToast(t('resource_deleted_success'));
      window.dispatchEvent(new CustomEvent('resource-deleted', {
        detail: { resourceId, projectId },
      }));
      navigate(`/projects/${projectId}`);
    } catch {
      showErrorToast(t('resource_delete_error'));
    }
  };

  const handleResourceTitleChange = async (newTitle: string): Promise<boolean> => {
    if (!token || !resourceId || !newTitle.trim() || newTitle === resource?.title) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: newTitle.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update resource title');
      }

      const trimmedTitle = newTitle.trim();
      setResource((prev) => prev ? { ...prev, title: trimmedTitle } : null);
      if (projectId) {
        window.dispatchEvent(new CustomEvent('resource-title-updated', {
          detail: { resourceId, projectId, title: trimmedTitle },
        }));
      }
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
      return false;
    }
  };

  const handleResourceUrlChange = async (newUrl: string): Promise<void> => {
    const resolvedUrl = parseResourceUrl(newUrl);
    if (!token || !resourceId || !resolvedUrl || resolvedUrl === resource?.url) {
      if (newUrl.trim() && !resolvedUrl) {
        showErrorToast(t('invalid_url'));
      }
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: resolvedUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update resource URL');
      }

      setResource((prev) => prev ? { ...prev, url: resolvedUrl } : null);
      showFieldSavedToast(t('resource_url_label'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
    }
  };

  const handleResourceDateChange = async (resourceDate: string | null): Promise<void> => {
    if (!token || !resourceId || resourceDate === resource?.resourceDate) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resourceDate: resourceDate?.trim() || null }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update date');
      }

      setResource((prev) => prev ? { ...prev, resourceDate: resourceDate?.trim() || null } : null);
      showFieldSavedToast(t('reference_date_label'));
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
    }
  };

  const handleNotesChange = async (notes: string | null): Promise<void> => {
    if (!token || !resourceId || notes === resource?.notes) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: notes?.trim() || null }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update notes');
      }

      setResource((prev) => prev ? { ...prev, notes: notes?.trim() || null } : null);
      setEntityRefsRefreshTrigger((prev) => prev + 1);
      const notesFieldLabel =
        resource && !resource.url && !resource.downloadUrl ? t('content_label') : t('Notes');
      showFieldSavedToast(notesFieldLabel);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
    }
  };

  const handleNotesChangeDebounced = (html: string): void => {
    latestNotesHtmlRef.current = html;
    const mentionedCharacterIds = extractMentionedEntityIds(html, 'character');
    const mentionedLocationIds = extractMentionedEntityIds(html, 'location');
    if (mentionedCharacterIds.length > 0) {
      setSelectedCharacterIds((prev) => Array.from(new Set([...prev, ...mentionedCharacterIds])));
    }
    if (mentionedLocationIds.length > 0) {
      setSelectedLocationIds((prev) => Array.from(new Set([...prev, ...mentionedLocationIds])));
    }

    if (notesSaveTimeoutRef.current) clearTimeout(notesSaveTimeoutRef.current);
    notesSaveTimeoutRef.current = setTimeout(() => {
      handleNotesChange(html.trim() || null);
      notesSaveTimeoutRef.current = null;
    }, 500);
  };

  const handleTagsChange = async (
    tags: string[],
    options?: { skipSavedToast?: boolean },
  ): Promise<void> => {
    if (!token || !resourceId) {
      return;
    }

    const normalizedTags = Array.from(
      new Set(tags.map(normalizeTag).filter((tag) => tag.length > 0))
    );

    if (JSON.stringify(normalizedTags) === JSON.stringify((resource?.tags || []).map(normalizeTag))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tags: normalizedTags }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as { error?: string }).error || 'Failed to update tags');
      }

      setResource((prev) => prev ? { ...prev, tags: normalizedTags } : null);
      if (!options?.skipSavedToast) {
        showFieldSavedToast(t('Tags'));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('Failed to load resource');
      showErrorToast(message);
    }
  };

  const handleAddTagFromKeyword = async (keyword: string): Promise<void> => {
    if (!resource) {
      return;
    }
    const nextTags = [...(resource.tags || []), keyword];
    await handleTagsChange(nextTags, { skipSavedToast: true });
    showToast(t('resource_tag_added_from_keyword_toast'));
  };

  const handleAnalysisComplete = async (): Promise<void> => {
    if (!projectId || !resourceId || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/resources/${resourceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setResource(data);
      }
    } catch (err) {
      console.error('Error refreshing resource after analysis:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <SidebarInset style={resourceInsetStyle} className="max-w-full">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 2rem', borderBottom: '1px solid #f3f4f6' }}>
          <SidebarToggleButton />
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          {t('loading')}
        </div>
      </SidebarInset>
    );
  }

  if (error || !resource) {
    return (
      <SidebarInset style={resourceInsetStyle} className="max-w-full">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 2rem', borderBottom: '1px solid #f3f4f6' }}>
          <SidebarToggleButton />
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>{error || t('Resource not found')}</p>
          <Button
            onClick={() => navigate(`/projects/${projectId}`)}
            style={{ marginTop: '1rem' }}
          >
            {t('Back to project')}
          </Button>
        </div>
      </SidebarInset>
    );
  }

  return (
    <SidebarInset style={resourceInsetStyle} className="max-w-full">
        {(!isViewableInBrowser(resource.mimeType, resource.url) || !isMaximized) && (
          <ViewHeader
            showSidebarToggle
            pageActions={
              !isMobile && canEditProject && projectId ? (
                <ResourceCreateActions projectId={projectId} />
              ) : undefined
            }
          >
            <EditableResourceHeader
              resourceTitle={resource.title}
              resourcesPath={`/projects/${projectId}/resources`}
              onResourceTitleChange={handleResourceTitleChange}
              readOnly={!canEditProject}
            />
          </ViewHeader>
        )}
        {isViewableInBrowser(resource.mimeType, resource.url) ? (
          <div style={{ display: 'flex', flexDirection: isMobile && !isMaximized ? 'column' : 'row', height: isMaximized ? APP_VIEWPORT_HEIGHT : (isMobile && !isMaximized ? 'auto' : appViewportHeightMinus('120px')), gap: isMaximized ? 0 : '1rem', padding: isMaximized ? 0 : (isMobile && !isMaximized ? '0 1rem 1rem' : '0 2rem 2rem') }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {!isMaximized && renderPreviewInfoPanel()}
            {!(resource.url && linkIframeFailed) && (
            <div
              ref={isMaximized ? maximizedContainerRef : undefined}
              tabIndex={isMaximized ? -1 : undefined}
              style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', backgroundColor: 'var(--card)', borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid var(--border)', minHeight: isMobile && !isMaximized ? '300px' : 'auto', height: isMobile && !isMaximized ? '400px' : '100%', position: 'relative', outline: 'none' }}
            >
              {renderFilePreview()}
              {!isMaximized ? (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ResourcePreviewOverlayButton
                        onClick={() => setIsMaximized(true)}
                        ariaLabel={t('maximize_resource')}
                        style={{ top: '0.75rem', right: '0.75rem' }}
                      >
                        <Expand className="h-5 w-5 text-foreground" />
                      </ResourcePreviewOverlayButton>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('maximize_resource')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <ResourcePreviewOverlayButton
                    onClick={() => setIsMaximized(false)}
                    ariaLabel={t('restore')}
                    style={{ top: '0.75rem', right: '0.75rem' }}
                  >
                    <Minimize className="h-5 w-5 text-foreground" />
                  </ResourcePreviewOverlayButton>
                  {previousGalleryResourceId && (
                    <ResourcePreviewOverlayButton
                      onClick={() => navigateToGalleryResource(previousGalleryResourceId)}
                      ariaLabel={t('previous_resource')}
                      style={{ top: '50%', left: '0.75rem', transform: 'translateY(-50%)' }}
                    >
                      <ChevronLeft className="h-5 w-5 text-foreground" />
                    </ResourcePreviewOverlayButton>
                  )}
                  {nextGalleryResourceId && (
                    <ResourcePreviewOverlayButton
                      onClick={() => navigateToGalleryResource(nextGalleryResourceId)}
                      ariaLabel={t('next_resource')}
                      style={{ top: '50%', right: '0.75rem', transform: 'translateY(-50%)' }}
                    >
                      <ChevronRight className="h-5 w-5 text-foreground" />
                    </ResourcePreviewOverlayButton>
                  )}
                </>
              )}
            </div>
            )}
            </div>
            {!isMaximized && (
            <div style={{ width: isMobile ? '100%' : '400px', flexShrink: 0, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Card>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                        {t('Notes')}:
                      </label>
                      <RichTextField
                        value={resource.notes || ''}
                        onChange={handleNotesChangeDebounced}
                        placeholder={t('Click to add notes...')}
                        minHeight="120px"
                        mentionableEntities={mentionableEntities}
                        editable={canEditProject}
                      />
                    </div>
                    {resourceId && projectId && (resource.mimeType || resource.url) && (
                      <AnalyzeResourceButton
                        resourceId={resourceId}
                        projectId={projectId}
                        mimeType={resource.mimeType}
                        isLinkResource={!!resource.url && !resource.downloadUrl}
                        initialAnalysis={resource.analysis}
                        initialAnalysisError={resource.analysisError}
                        resourceTags={resource.tags}
                        readOnly={!canEditProject}
                        onAnalysisComplete={handleAnalysisComplete}
                        onAnalysisFailed={handleAnalysisComplete}
                        onAnalysisUpdated={(analysis) => {
                          setResource((prev) => (prev ? { ...prev, analysis } : null));
                        }}
                        onAddTagFromKeyword={handleAddTagFromKeyword}
                      />
                    )}
                    {projectId && (
                      <ProjectTagsField
                        projectId={projectId}
                        tags={resource.tags || []}
                        onTagsChange={handleTagsChange}
                        disabled={!canEditProject}
                        compact
                        linkTagsToResourcesList
                      />
                    )}
                    <EntityTagsField
                      label={t('filter_characters')}
                      entityType="character"
                      options={characterOptions}
                      selectedIds={selectedCharacterIds}
                      onChange={(ids) => handleEntityTagsChange('character', ids)}
                      disabled={!canEditProject}
                      emptyHint={t('no_characters_yet_hint')}
                      showEditTooltip
                    />
                    <EntityTagsField
                      label={t('filter_locations')}
                      entityType="location"
                      options={locationOptions}
                      selectedIds={selectedLocationIds}
                      onChange={(ids) => handleEntityTagsChange('location', ids)}
                      disabled={!canEditProject}
                      emptyHint={t('no_locations_yet_hint')}
                      showEditTooltip
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {t('reference_date_label')}
                        </label>
                        <InfoHint label={t('reference_date_label')} iconClassName="h-3.5 w-3.5">
                          {t('reference_date_tooltip')}
                        </InfoHint>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <DatePicker
                            value={resource.resourceDate ?? ''}
                            onChange={(v) => {
                              void handleResourceDateChange(v.trim() ? v.trim() : null);
                            }}
                            allowEmpty
                            disabled={!canEditProject}
                          />
                        </div>
                        {canEditProject && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={!resource.resourceDate}
                                  onClick={() => setIsAddTimelineModalOpen(true)}
                                  style={{ flexShrink: 0 }}
                                >
                                  <Plus className="h-4 w-4" />
                                  {t('resource_add_timeline_item')}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{t('resource_add_timeline_item_tooltip')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        )}
                      </div>
                    </div>
                    {(otherLinksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
                      <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                          {t('related_entities')}
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {otherLinksTo.length > 0 && (
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                                {t('links_to')}
                              </span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {otherLinksTo.map((e) => (
                                  <RelatedEntityLink
                                    key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                    entity={e}
                                    projectId={projectId ?? ''}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {relatedEntities.referencedIn.length > 0 && (
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                                {t('referenced_in')}
                              </span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {relatedEntities.referencedIn.map((e) => (
                                  <RelatedEntityLink
                                    key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                    entity={e}
                                    projectId={projectId ?? ''}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <Separator />
                    <MetadataRow label={t('File name')}>{resource.fileName}</MetadataRow>
                    {resource.fileSize !== null && (
                      <MetadataRow label={t('File size')}>
                        {formatFileSize(resource.fileSize)}
                        {resource.wordCount !== null && ` (${resource.wordCount} ${t('word_plural')})`}
                      </MetadataRow>
                    )}
                    <MetadataRow label={t('created')}>{new Date(resource.createdAt).toLocaleString()}</MetadataRow>
                    <MetadataRow label={t('updated')}>{new Date(resource.updatedAt).toLocaleString()}</MetadataRow>
                    <ResourceDetailActions
                      canDelete={canEditProject}
                      canDownload={Boolean(resource.downloadUrl)}
                      onDelete={() => setIsDeleteDialogOpen(true)}
                      onDownload={handleDownload}
                      deleteLabel={t('delete_resource')}
                      downloadLabel={t('Download')}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            )}
          </div>
        ) : isGenericResource ? (
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', padding: isMobile ? '0 1rem 1rem' : '0 2rem 2rem', minHeight: 0, flex: 1 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Card>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      {t('content_label')}:
                    </label>
                    <RichTextField
                      value={resource.notes || ''}
                      onChange={handleNotesChangeDebounced}
                      placeholder={t('Click to add notes...')}
                      minHeight="200px"
                      mentionableEntities={mentionableEntities}
                      editable={canEditProject}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: isMobile ? '100%' : '400px', flexShrink: 0, alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Card>
                <CardContent style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {projectId && (
                      <ProjectTagsField
                        projectId={projectId}
                        tags={resource.tags || []}
                        onTagsChange={handleTagsChange}
                        disabled={!canEditProject}
                        compact
                        linkTagsToResourcesList
                      />
                    )}
                    <EntityTagsField
                      label={t('filter_characters')}
                      entityType="character"
                      options={characterOptions}
                      selectedIds={selectedCharacterIds}
                      onChange={(ids) => handleEntityTagsChange('character', ids)}
                      disabled={!canEditProject}
                      emptyHint={t('no_characters_yet_hint')}
                      showEditTooltip
                    />
                    <EntityTagsField
                      label={t('filter_locations')}
                      entityType="location"
                      options={locationOptions}
                      selectedIds={selectedLocationIds}
                      onChange={(ids) => handleEntityTagsChange('location', ids)}
                      disabled={!canEditProject}
                      emptyHint={t('no_locations_yet_hint')}
                      showEditTooltip
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                          {t('reference_date_label')}
                        </label>
                        <InfoHint label={t('reference_date_label')} iconClassName="h-3.5 w-3.5">
                          {t('reference_date_tooltip')}
                        </InfoHint>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <DatePicker
                            value={resource.resourceDate ?? ''}
                            onChange={(v) => {
                              void handleResourceDateChange(v.trim() ? v.trim() : null);
                            }}
                            allowEmpty
                            disabled={!canEditProject}
                          />
                        </div>
                        {canEditProject && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={!resource.resourceDate}
                                  onClick={() => setIsAddTimelineModalOpen(true)}
                                  style={{ flexShrink: 0 }}
                                >
                                  <Plus className="h-4 w-4" />
                                  {t('resource_add_timeline_item')}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{t('resource_add_timeline_item_tooltip')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        )}
                      </div>
                    </div>
                    {(otherLinksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
                      <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                          {t('related_entities')}
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {otherLinksTo.length > 0 && (
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                                {t('links_to')}
                              </span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {otherLinksTo.map((e) => (
                                  <RelatedEntityLink
                                    key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                    entity={e}
                                    projectId={projectId ?? ''}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {relatedEntities.referencedIn.length > 0 && (
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                                {t('referenced_in')}
                              </span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {relatedEntities.referencedIn.map((e) => (
                                  <RelatedEntityLink
                                    key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                    entity={e}
                                    projectId={projectId ?? ''}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <Separator />
                    <MetadataRow label={t('created')}>{new Date(resource.createdAt).toLocaleString()}</MetadataRow>
                    <MetadataRow label={t('updated')}>{new Date(resource.updatedAt).toLocaleString()}</MetadataRow>
                    <ResourceDetailActions
                      canDelete={canEditProject}
                      canDownload={Boolean(resource.downloadUrl)}
                      onDelete={() => setIsDeleteDialogOpen(true)}
                      onDownload={handleDownload}
                      deleteLabel={t('delete_resource')}
                      downloadLabel={t('Download')}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div style={{ padding: '0 2rem 2rem', maxWidth: '800px', margin: '0 auto' }}>
            <Card>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                      {t('Notes')}:
                    </label>
                    <RichTextField
                      value={resource.notes || ''}
                      onChange={handleNotesChangeDebounced}
                      placeholder={t('Click to add notes...')}
                      minHeight="120px"
                      mentionableEntities={mentionableEntities}
                      editable={canEditProject}
                    />
                  </div>
                  {resourceId && projectId && (resource.mimeType || resource.url) && (
                    <AnalyzeResourceButton
                      resourceId={resourceId}
                      projectId={projectId}
                      mimeType={resource.mimeType}
                      isLinkResource={!!resource.url && !resource.downloadUrl}
                      initialAnalysis={resource.analysis}
                      initialAnalysisError={resource.analysisError}
                      resourceTags={resource.tags}
                      readOnly={!canEditProject}
                      onAnalysisComplete={handleAnalysisComplete}
                      onAnalysisFailed={handleAnalysisComplete}
                      onAnalysisUpdated={(analysis) => {
                        setResource((prev) => (prev ? { ...prev, analysis } : null));
                      }}
                      onAddTagFromKeyword={handleAddTagFromKeyword}
                    />
                  )}
                  {projectId && (
                    <ProjectTagsField
                      projectId={projectId}
                      tags={resource.tags || []}
                      onTagsChange={handleTagsChange}
                      disabled={!canEditProject}
                      compact
                      linkTagsToResourcesList
                    />
                  )}
                  <EntityTagsField
                    label={t('filter_characters')}
                    entityType="character"
                    options={characterOptions}
                    selectedIds={selectedCharacterIds}
                    onChange={(ids) => handleEntityTagsChange('character', ids)}
                    disabled={!canEditProject}
                    emptyHint={t('no_characters_yet_hint')}
                    showEditTooltip
                  />
                  <EntityTagsField
                    label={t('filter_locations')}
                    entityType="location"
                    options={locationOptions}
                    selectedIds={selectedLocationIds}
                    onChange={(ids) => handleEntityTagsChange('location', ids)}
                    disabled={!canEditProject}
                    emptyHint={t('no_locations_yet_hint')}
                    showEditTooltip
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
                      <label style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                        {t('reference_date_label')}
                      </label>
                      <InfoHint label={t('reference_date_label')} iconClassName="h-3.5 w-3.5">
                        {t('reference_date_tooltip')}
                      </InfoHint>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <DatePicker
                          value={resource.resourceDate ?? ''}
                          onChange={(v) => {
                            void handleResourceDateChange(v.trim() ? v.trim() : null);
                          }}
                          allowEmpty
                          disabled={!canEditProject}
                        />
                      </div>
                      {canEditProject && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={!resource.resourceDate}
                                onClick={() => setIsAddTimelineModalOpen(true)}
                                style={{ flexShrink: 0 }}
                              >
                                <Plus className="h-4 w-4" />
                                {t('resource_add_timeline_item')}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>{t('resource_add_timeline_item_tooltip')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      )}
                    </div>
                  </div>
                  {(otherLinksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                        {t('related_entities')}
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {otherLinksTo.length > 0 && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                              {t('links_to')}
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {otherLinksTo.map((e) => (
                                <RelatedEntityLink
                                  key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                  entity={e}
                                  projectId={projectId ?? ''}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {relatedEntities.referencedIn.length > 0 && (
                          <div>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                              {t('referenced_in')}
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {relatedEntities.referencedIn.map((e) => (
                                <RelatedEntityLink
                                  key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                                  entity={e}
                                  projectId={projectId ?? ''}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Separator />
                  <MetadataRow label={t('File name')}>{resource.fileName}</MetadataRow>
                  {resource.fileSize !== null && (
                    <MetadataRow label={t('File size')}>
                      {formatFileSize(resource.fileSize)}
                      {resource.wordCount !== null && ` (${resource.wordCount} ${t('word_plural')})`}
                    </MetadataRow>
                  )}
                  <MetadataRow label={t('created')}>{new Date(resource.createdAt).toLocaleString()}</MetadataRow>
                  <MetadataRow label={t('updated')}>{new Date(resource.updatedAt).toLocaleString()}</MetadataRow>
                  <ResourceDetailActions
                    canDelete={canEditProject}
                    canDownload={Boolean(resource.downloadUrl)}
                    onDelete={() => setIsDeleteDialogOpen(true)}
                    onDownload={handleDownload}
                    deleteLabel={t('delete_resource')}
                    downloadLabel={t('Download')}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {projectId && (
          <AddTimelineEventModal
            isOpen={isAddTimelineModalOpen}
            onClose={() => setIsAddTimelineModalOpen(false)}
            projectId={projectId}
            onSuccess={() => {
              setIsAddTimelineModalOpen(false);
              setEntityRefsRefreshTrigger((p) => p + 1);
            }}
            defaultsForNewItem={
              isAddTimelineModalOpen && resource?.resourceDate
                ? {
                    name: resource.title,
                    description: buildResourceMentionDescription(resource),
                    item_type: 'historical_event',
                    tags: [],
                    start_date: `${resource.resourceDate}T12:00:00.000Z`,
                    start_precision: 'day',
                    end_date: null,
                    end_precision: null,
                  }
                : undefined
            }
          />
        )}
        <ConfirmDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          title={t('delete_resource')}
          description={t('resource_delete_confirm')}
          confirmLabel={t('delete_resource')}
          onConfirm={() => {
            void handleDelete();
          }}
        />
    </SidebarInset>
  );
};
